import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { formatPrice, workspacesEnabled } from "ui";
import {
  ProfileFirstActions,
  ProfileInterface,
  UserProfileRole,
} from "ts-interfaces";
import { AuthContext } from "../auth/Auth";
import { useLoginState } from "../studioxr/auth/LoginState.ts";
import { useMattoState } from "../MattoState.ts";
import { setProfileFirstAction } from "../libs/util.ts";
import { trackEvent } from "../libs/analyticsManager";
import { teamAnalytics } from "../team/teamAnalytics";
import { useHasTeam } from "../hooks/useShowTeamsUpsell";
import { ONE_TEAM_ONLY } from "../workspace/workspaceStrings";
import { getGoogleAnalyticsProductInfo } from "./config.js";
import { ProfilePrices } from "./useStripeStore.tsx";
import {
  ANNUAL_PRO_CREDITS,
  MONTHLY_PRO_CREDITS,
} from "../constants/proCredits";
import { MIN_TEAM_SEATS } from "../team/create/types";
import { useFeatureFlag } from "../featureFlags/useFeatureFlag";
import { STUDIO_LIBRARY_FLAG } from "../libs/studioLibraryUse";

/**
 * First step of the Upgrade to Pro modal: Free / Pro / Pro Teams cards.
 * "Upgrade to Pro" continues to the monthly/yearly picker (`onUpgrade`);
 * "Create your team" swaps the modal to the Create Team wizard.
 * Breakpoints follow the design: xs full-screen sheet, sm stacked two-column
 * cards, md+ three columns with subgrid-aligned buttons.
 */

export type PlanPair = {
  monthly?: ProfilePrices | null;
  yearly?: ProfilePrices | null;
};

interface PlanOverviewProps {
  proPrices: PlanPair;
  teamPrices: PlanPair;
  onUpgrade: () => void;
  /** False when reopened from the team wizard: the modal view was already counted. */
  trackView?: boolean;
}

const INK = "#111114";
const MUTED = "#6b6b74";
const BODY = "#2a2a30";

const fmt = (p?: ProfilePrices | null): string | null =>
  p?.local_price != null && p.currency
    ? formatPrice(Number(p.local_price), p.currency, false)
    : null;

const CheckIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    style={{ flex: "none", marginTop: 3 }}
  >
    <path
      d="M3 8.5l3.2 3L13 4.5"
      stroke="#7b55e0"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Pill = ({
  children,
  variant,
}: {
  children: React.ReactNode;
  variant: "grey" | "purple" | "outline";
}) => (
  <Box
    component="span"
    sx={{
      fontSize: "12px",
      fontWeight: 700,
      borderRadius: "999px",
      padding: "3px 10px",
      whiteSpace: "nowrap",
      ...(variant === "grey" && { color: "#3a3a40", background: "#f1f1f4" }),
      ...(variant === "purple" && { color: "#6b3fd4", background: "#ece5fd" }),
      ...(variant === "outline" && {
        color: "#6b3fd4",
        background: "#fff",
        border: "1px solid #e0d7fb",
        padding: "2px 9px",
      }),
    }}
  >
    {children}
  </Box>
);

const ctaSx = {
  width: "100%",
  height: { xs: "48px", sm: "44px", lg: "48px" },
  borderRadius: "10px",
  fontWeight: 700,
  fontSize: { xs: "16px", sm: "15px", lg: "16px" },
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { boxShadow: "none" },
};

interface CardProps {
  title: string;
  badge?: React.ReactNode;
  subtitle?: string;
  features: React.ReactNode[];
  price?: { headline: string; sub?: string | null };
  cta: React.ReactNode;
  highlighted?: boolean;
  hideCtaOnMobile?: boolean;
}

const PlanCard = ({
  title,
  badge,
  subtitle,
  features,
  price,
  cta,
  highlighted,
  hideCtaOnMobile,
}: CardProps) => {
  const divider = highlighted ? "#e2def0" : "#e6e6ea";
  // Card padding minus the extra 1px of the highlighted border keeps all three cards the same size.
  const pad = (n: number) => `${highlighted ? n - 1 : n}px`;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "minmax(0,1fr)",
          sm: "minmax(0,1fr) 220px",
          md: "minmax(0,1fr)",
        },
        gridTemplateRows: { md: "subgrid" },
        gridRow: { md: "span 3" },
        columnGap: { sm: "24px", md: 0 },
        border: highlighted ? `2px solid ${INK}` : "1px solid #e4e4e8",
        background: highlighted ? "#f7f5ff" : "#fff",
        borderRadius: { xs: "12px", lg: "14px" },
        padding: { xs: pad(18), sm: pad(20), lg: pad(24) },
        boxSizing: "border-box",
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          gridColumn: "1 / -1",
          display: "flex",
          flexDirection: "column",
          gap: "2px",
          paddingBottom: { xs: "14px", md: "18px", lg: "24px" },
          borderBottom: `1px solid ${divider}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: "8px", lg: "10px" },
            flexWrap: "wrap",
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: { xs: "18px", lg: "20px" },
              lineHeight: { xs: "26px", lg: "28px" },
              fontWeight: 800,
              color: INK,
            }}
          >
            {title}
          </Typography>
          {badge}
        </Box>
        {subtitle && (
          <Typography
            component="span"
            sx={{
              fontSize: { xs: "14px", lg: "15px" },
              lineHeight: { xs: "20px", lg: "22px" },
              color: MUTED,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      <Stack
        component="ul"
        sx={{
          listStyle: "none",
          margin: 0,
          padding: 0,
          paddingTop: { xs: "14px", md: "18px", lg: "24px" },
          paddingBottom: { md: "28px", lg: "36px" },
          gap: { xs: "10px", md: "12px", lg: "14px" },
          gridColumn: 1,
        }}
      >
        {features.map((f, i) => (
          <Box
            component="li"
            key={i}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: { xs: "10px", lg: "12px" },
              fontSize: { xs: "15px", sm: "14px", lg: "15px" },
              lineHeight: { xs: "22px", sm: "20px", lg: "22px" },
              color: BODY,
            }}
          >
            <CheckIcon />
            <Box
              component="span"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flexWrap: "wrap",
              }}
            >
              {f}
            </Box>
          </Box>
        ))}
      </Stack>

      <Box
        sx={{
          display: { xs: hideCtaOnMobile ? "none" : "flex", sm: "flex" },
          flexDirection: "column",
          justifyContent: "flex-end",
          gap: { xs: "12px", lg: "16px" },
          gridColumn: { sm: 2, md: 1 },
          alignSelf: "end",
          paddingTop: { xs: "20px", sm: "14px", md: 0 },
        }}
      >
        {price && (
          <Box>
            <Typography
              sx={{
                fontSize: { xs: "17px", sm: "16px", lg: "17px" },
                lineHeight: { xs: "24px", sm: "22px", lg: "24px" },
                fontWeight: 800,
                color: INK,
              }}
            >
              {price.headline}
            </Typography>
            {price.sub && (
              <Typography
                sx={{ fontSize: "13px", lineHeight: "19px", color: MUTED }}
              >
                {price.sub}
              </Typography>
            )}
          </Box>
        )}
        {cta}
      </Box>
    </Box>
  );
};

export const PlanOverview = ({
  proPrices,
  teamPrices,
  onUpgrade,
  trackView = true,
}: PlanOverviewProps) => {
  const profile: ProfileInterface | any = useLoginState(
    (state) => state.profile,
  );
  const setOrCreateProfile = useLoginState((state) => state.setOrCreateProfile);
  const showSubscription: any = useMattoState(
    (state) => state.showSubscription,
  );
  const setShowSubscription = useMattoState(
    (state) => state.setShowSubscription,
  );
  const { currentUser } = useContext(AuthContext);

  // The picker that follows reads both Prices, so neither may be a placeholder.
  const priceReady = Boolean(
    proPrices.monthly?.productID && proPrices.yearly?.productID,
  );
  const [processing, setProcessing] = useState(false);
  const upgradeDisabled = processing || !priceReady;

  // Same analytics as the intro step this screen replaces: once per open, even if prices never load.
  const hasTrackedView = useRef(false);
  useEffect(() => {
    if (!trackView || hasTrackedView.current) return;
    hasTrackedView.current = true;
    setProfileFirstAction(ProfileFirstActions.OPEN_PAYMENT_MODAL).finally(
      () => {},
    );
    try {
      if (window.gtag)
        window.gtag(
          "event",
          "view_item",
          getGoogleAnalyticsProductInfo(proPrices.monthly),
        );
    } catch (error) {
      console.error("PlanOverview view_item", error);
    }
  }, []);

  const handleUpgrade = async () => {
    if (!profile?.id) {
      if (!currentUser?.uid || !currentUser?.email) {
        window.alert(
          "Something went wrong! you cannot proceed with the process. Please contact studio@mattoboard.com.",
        );
        return;
      }
      setProcessing(true);
      try {
        await setOrCreateProfile(
          { user: { uid: currentUser.uid, email: currentUser.email } },
          false,
        );
      } catch (err) {
        console.error("PlanOverview setOrCreateProfile", err);
        setProcessing(false);
        return;
      }
    }
    onUpgrade();
  };

  const handleCreateTeam = () => {
    trackEvent("Subscription - Create Team Click");
    teamAnalytics.createStarted("plan_overview");
    setShowSubscription("createTeam");
  };
  // Same gate as the ?modal=team deep link: a disabled deployment has no team pages to land on.
  const teamsAvailable = workspacesEnabled();
  // One team per user for now: a team member keeps the card but can't create a second team.
  const onTeam = useHasTeam() === true;
  const studioLibraryLive = useFeatureFlag(STUDIO_LIBRARY_FLAG);
  // "Your plan today" marks the personal plan: a personal-Pro user lands here from a gate in a
  // team workspace whose subscription lapsed, and must not be offered a second Pro subscription.
  const personalPro = profile?.userRole === UserProfileRole.PRO;
  const currentPlanBadge = <Pill variant="grey">Your plan today</Pill>;
  const currentPlanCta = (
    <Button
      disabled
      variant="contained"
      sx={{
        ...ctaSx,
        "&.Mui-disabled": { background: "#f2f2f4", color: "#9b9ba3" },
      }}
    >
      Current plan
    </Button>
  );

  const warning =
    Array.isArray(showSubscription) && showSubscription.length > 1
      ? showSubscription[1]
      : "";

  const proYearly = fmt(proPrices.yearly);
  const proMonthly = fmt(proPrices.monthly);
  const teamYearly = fmt(teamPrices.yearly);
  const teamMonthly = fmt(teamPrices.monthly);

  const proPrice = proYearly
    ? {
        headline: `${proYearly} per year`,
        sub: proMonthly ? `or ${proMonthly} per month` : null,
      }
    : proMonthly
      ? { headline: `${proMonthly} per month` }
      : undefined;
  const teamPrice = teamYearly
    ? {
        headline: `${teamYearly} per seat per year, from ${MIN_TEAM_SEATS} seats`,
        sub: teamMonthly ? `or ${teamMonthly} per seat per month` : null,
      }
    : teamMonthly
      ? {
          headline: `${teamMonthly} per seat per month, from ${MIN_TEAM_SEATS} seats`,
        }
      : undefined;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        overflowY: "auto",
        overflowX: "hidden",
        boxSizing: "border-box",
        padding: {
          xs: 0,
          sm: "28px",
          md: "32px 32px 24px",
          lg: "40px 40px 32px",
        },
      }}
    >
      <Box
        sx={{
          position: { xs: "sticky", sm: "static" },
          top: 0,
          zIndex: 1,
          background: "#fff",
          minHeight: { xs: "56px", sm: "auto" },
          display: "flex",
          alignItems: "center",
          padding: { xs: "0 56px 0 16px", sm: 0 },
          paddingRight: { sm: "48px" },
          borderBottom: { xs: "1px solid #ececf0", sm: "none" },
          boxSizing: "border-box",
        }}
      >
        <Typography
          component="h2"
          sx={{
            margin: 0,
            fontSize: { xs: "20px", sm: "24px", lg: "28px" },
            lineHeight: { xs: "26px", sm: "30px", lg: "34px" },
            fontWeight: 800,
            color: INK,
          }}
        >
          Design without limits
        </Typography>
      </Box>

      <Box sx={{ padding: { xs: "16px 16px 28px", sm: 0 } }}>
        {warning && (
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "16px",
              color: "error.main",
              marginTop: { xs: 0, sm: "12px" },
            }}
          >
            {warning}
          </Typography>
        )}

        <Box
          sx={{
            marginTop: { xs: 0, sm: "24px", lg: "32px" },
            display: { xs: "flex", md: "grid" },
            flexDirection: "column",
            gap: { xs: "12px", md: 0 },
            gridTemplateColumns: {
              md: `repeat(${teamsAvailable ? 3 : 2}, minmax(0, 1fr))`,
            },
            gridTemplateRows: { md: "auto 1fr auto" },
            columnGap: { md: "12px", lg: "16px" },
          }}
        >
          <PlanCard
            title="Free"
            badge={personalPro ? undefined : currentPlanBadge}
            features={[
              "Boards and the materials library",
              "A few credits to try AI concepts",
              "Share by link",
            ]}
            hideCtaOnMobile
            cta={personalPro ? null : currentPlanCta}
          />
          <PlanCard
            title="Pro"
            badge={personalPro ? currentPlanBadge : undefined}
            subtitle="For you"
            features={[
              "Everything in Free",
              `${ANNUAL_PRO_CREDITS} credits a year, or ${MONTHLY_PRO_CREDITS} a month`,
              "Downloads, uploads and high-quality renders",
              "Projects and schedules",
            ]}
            price={proPrice}
            cta={
              personalPro ? (
                currentPlanCta
              ) : (
                <Button
                  data-testid="upgrade-to-pro"
                  variant="outlined"
                  onClick={handleUpgrade}
                  disabled={upgradeDisabled}
                  startIcon={
                    !priceReady ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                  sx={{
                    ...ctaSx,
                    border: `1.5px solid ${INK}`,
                    color: INK,
                    background: "#fff",
                    "&:hover": {
                      background: "#f6f6f8",
                      border: `1.5px solid ${INK}`,
                    },
                    "&.Mui-disabled": {
                      border: "1.5px solid #cfcfd6",
                      color: "#9b9ba3",
                    },
                  }}
                >
                  {processing ? "Processing...Please Wait" : "Upgrade to Pro"}
                </Button>
              )
            }
          />
          {teamsAvailable && (
            <PlanCard
              title="Pro Teams"
              badge={<Pill variant="purple">Recommended</Pill>}
              subtitle="For you and your colleagues"
              highlighted
              features={[
                "Everything in Pro, for each person",
                "Team boards, shared to view or edit",
                "One pool of credits for the team",
                "People, seats and billing on one page",
                studioLibraryLive ? (
                  "Studio library"
                ) : (
                  <>
                    Studio library <Pill variant="outline">Coming soon</Pill>
                  </>
                ),
              ]}
              price={teamPrice}
              cta={
                <>
                  {/* Above the button, so all three cards' buttons stay aligned. */}
                  {onTeam && (
                    <Typography
                      sx={{
                        fontSize: "13px",
                        lineHeight: "19px",
                        color: MUTED,
                      }}
                    >
                      {ONE_TEAM_ONLY}
                    </Typography>
                  )}
                  <Button
                    variant="contained"
                    onClick={handleCreateTeam}
                    disabled={onTeam}
                    sx={{
                      ...ctaSx,
                      background: INK,
                      color: "#fff",
                      "&:hover": { background: "#2a2a30", boxShadow: "none" },
                      "&.Mui-disabled": {
                        background: "#f2f2f4",
                        color: "#9b9ba3",
                      },
                    }}
                  >
                    Create your team
                  </Button>
                </>
              }
            />
          )}
        </Box>

        <Typography
          sx={{
            marginTop: { xs: "8px", sm: "20px", lg: "28px" },
            textAlign: "center",
            fontSize: { xs: "14px", lg: "15px" },
            lineHeight: { xs: "20px", lg: "22px" },
            color: "#55555c",
          }}
        >
          {teamsAvailable
            ? "Not sure? Start with Pro and add your team when you need it."
            : "Cancel anytime."}
        </Typography>
      </Box>
    </Box>
  );
};
